import React, { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import CodeBlock from "./CodeBlock";

interface MarkdownRendererProps {
  content: string;
}

function MarkdownRendererInner({ content }: MarkdownRendererProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={{
        // Inline code only — block code is handled by `pre` below
        code({ className, children, ...props }) {
          // If this code element has a language class, it's inside a <pre> and
          // will be handled by the `pre` override. Return it as-is so the
          // highlighted children (React elements from rehype-highlight) pass through.
          if (className && /language-/.test(className)) {
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }

          return (
            <code
              className="px-1 py-0.5 rounded text-[13px] font-mono"
              style={{ backgroundColor: "var(--c-surface-2)" }}
              {...props}
            >
              {children}
            </code>
          );
        },

        // Fenced code via pre — wraps with CodeBlock and passes React children
        // (including rehype-highlight spans) directly for rendering
        pre({ children }) {
          const child = React.Children.only(children) as React.ReactElement;
          const className = child?.props?.className || "";
          const match = /language-(\w+)/.exec(className);

          if (match) {
            return <CodeBlock language={match[1]}>{child.props.children}</CodeBlock>;
          }
          return <pre className="my-2">{children}</pre>;
        },

        // Headers
        h1: ({ children }) => (
          <h1
            className="text-xl font-semibold mt-4 mb-2"
            style={{ color: "var(--c-text)", letterSpacing: "-0.02em" }}
          >
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2
            className="text-lg font-semibold mt-3 mb-1.5"
            style={{ color: "var(--c-text)", letterSpacing: "-0.01em" }}
          >
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-base font-medium mt-3 mb-1" style={{ color: "var(--c-text)" }}>
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-sm font-medium mt-2 mb-1" style={{ color: "var(--c-text)" }}>
            {children}
          </h4>
        ),

        // Paragraphs
        p: ({ children }) => (
          <p className="my-1.5 leading-relaxed whitespace-pre-wrap">{children}</p>
        ),

        // Lists
        ul: ({ children }) => (
          <ul className="my-1.5 ml-4 space-y-0.5 list-disc" style={{ color: "var(--c-text)" }}>
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="my-1.5 ml-4 space-y-0.5 list-decimal" style={{ color: "var(--c-text)" }}>
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="leading-relaxed pl-0.5">{children}</li>,

        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 transition-colors"
            style={{ color: "var(--c-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-primary)")}
          >
            {children}
          </a>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote
            className="my-2 pl-3 py-1"
            style={{
              borderLeft: "3px solid var(--c-primary)",
              backgroundColor: "var(--c-primary-subtle)",
              borderRadius: "0 4px 4px 0",
              color: "var(--c-text-secondary)",
            }}
          >
            {children}
          </blockquote>
        ),

        // Tables
        table: ({ children }) => (
          <div
            className="my-3 overflow-x-auto rounded-lg"
            style={{ border: "1px solid var(--c-border)" }}
          >
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ backgroundColor: "var(--c-surface-2)" }}>{children}</thead>
        ),
        th: ({ children }) => (
          <th
            className="px-3 py-2 text-left text-xs font-medium"
            style={{ color: "var(--c-text-secondary)", borderBottom: "1px solid var(--c-border)" }}
          >
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td
            className="px-3 py-2 text-sm"
            style={{ color: "var(--c-text)", borderBottom: "1px solid var(--c-border)" }}
          >
            {children}
          </td>
        ),

        // Horizontal rule
        hr: () => <hr className="my-4" style={{ borderColor: "var(--c-border)" }} />,

        // Strikethrough
        del: ({ children }) => <del style={{ color: "var(--c-text-faint)" }}>{children}</del>,

        // Images
        img: ({ src, alt }) => (
          <img
            src={src}
            alt={alt || ""}
            className="rounded-lg max-w-full my-2"
            style={{ border: "1px solid var(--c-border)" }}
          />
        ),

        // Task list checkboxes
        input: ({ type, checked, ...props }) => {
          if (type === "checkbox") {
            return (
              <input
                type="checkbox"
                checked={checked}
                readOnly
                className="mr-1.5"
                style={{ accentColor: "var(--c-primary)" }}
                {...props}
              />
            );
          }
          return <input type={type} {...props} />;
        },

        // Strong and emphasis
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

const MarkdownRenderer = memo(MarkdownRendererInner, (prev, next) => {
  return prev.content === next.content;
});

export default MarkdownRenderer;
